import { useQuery } from "@tanstack/react-query";
import { fetchAllFilteredRows } from "@/components/shared/data-table/data-table-export";
import { getAccountRelated } from "@/features/accounts/hooks/account-client";
import type { Account } from "@/features/accounts/types";
import { accountKeys } from "@/features/accounts/hooks/query-keys";
import { type AccountListParams, getAccounts, getAccountSummaryCounts } from "@/lib/db/accounts";

interface UseAccountsOptions extends AccountListParams {
  enabled?: boolean;
}

export function useAccounts(options?: UseAccountsOptions) {
  const queryFilters: AccountListParams = {
    limit: options?.limit ?? 50,
    offset: Math.max(0, options?.offset ?? 0),
    search: options?.search,
    sort_direction: options?.sort_direction,
    sort_field: options?.sort_field,
    type: options?.type,
  };

  return useQuery({
    enabled: options?.enabled ?? true,
    queryKey: accountKeys.list({ ...queryFilters }),
    queryFn: () => getAccounts(queryFilters),
  });
}

/** Every account the current filters select, for CSV export. */
export function fetchAccountsForExport(filters: AccountListParams): Promise<Account[]> {
  return fetchAllFilteredRows<Account>(({ limit, offset }) =>
    getAccounts({ ...filters, limit, offset }),
  );
}

/**
 * The account totals the list summary reports.
 *
 * Keyed by the search alone, so stepping through pages or switching scope
 * leaves the figures — and the request count — where they are.
 */
export function useAccountSummaryCounts(search?: string) {
  return useQuery({
    queryKey: accountKeys.counts({ search: search ?? null }),
    queryFn: () => getAccountSummaryCounts({ search }),
  });
}

export function useAccountRelated(id: string) {
  return useQuery({
    enabled: Boolean(id),
    queryKey: accountKeys.detail(id || "missing"),
    queryFn: () => getAccountRelated(id || ""),
  });
}
